<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';

defineProps({
  events: {
    type: Array,
    default: () => [],
  },
});

const { t, te } = useI18n();

const expanded = ref([]);

const LEVEL_CLASSES = {
  info: 'bg-n-slate-8',
  warn: 'bg-n-amber-9',
  error: 'bg-n-ruby-9',
};

// Event names are dotted and lower snake (`meta.read_source_waba`); the i18n keys are
// upper snake. An unknown name falls back to itself so a new backend event is still
// readable in the audit trail before it gets a translation.
const label = event => {
  const key = `CLIENT_SETTINGS.CURRENCY_MIGRATION.EVENTS.${event.name
    .toUpperCase()
    .replace(/\./g, '_')}`;
  return te(key) ? t(key) : event.name;
};

const timestamp = value =>
  new Date(value).toLocaleString(undefined, {
    dateStyle: 'short',
    timeStyle: 'medium',
  });

const hasPayload = event =>
  Object.keys(event.request_payload || {}).length > 0 ||
  Object.keys(event.response_payload || {}).length > 0;

const isExpanded = id => expanded.value.includes(id);

const toggle = id => {
  expanded.value = isExpanded(id)
    ? expanded.value.filter(entry => entry !== id)
    : [...expanded.value, id];
};

const pretty = payload => JSON.stringify(payload, null, 2);
</script>

<template>
  <section class="flex flex-col gap-4">
    <h2 class="text-lg font-medium text-n-slate-12">
      {{ $t('CLIENT_SETTINGS.CURRENCY_MIGRATION.TIMELINE.TITLE') }}
    </h2>

    <p v-if="!events.length" class="text-sm text-n-slate-11">
      {{ $t('CLIENT_SETTINGS.CURRENCY_MIGRATION.TIMELINE.EMPTY') }}
    </p>

    <ol v-else class="flex flex-col m-0 list-none" data-testid="migration-timeline">
      <li
        v-for="event in events"
        :key="event.id"
        class="flex gap-3"
        :data-testid="`timeline-event-${event.name}`"
      >
        <!-- The rail: a dot per event and a line joining it to the next one. -->
        <div class="flex flex-col items-center gap-1 pt-1.5">
          <span
            class="rounded-full size-2 shrink-0"
            :class="LEVEL_CLASSES[event.level] ?? LEVEL_CLASSES.info"
          />
          <span class="flex-1 w-px bg-n-weak" />
        </div>

        <div class="flex flex-col flex-1 min-w-0 gap-1 pb-4">
          <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span
              class="text-sm font-medium"
              :class="
                event.level === 'error' ? 'text-n-ruby-11' : 'text-n-slate-12'
              "
            >
              {{ label(event) }}
            </span>
            <span class="font-mono text-xs text-n-slate-10">
              {{ timestamp(event.created_at) }}
            </span>
            <span
              v-if="event.http_status"
              class="px-1.5 py-0.5 font-mono text-xs rounded text-n-slate-11 bg-n-alpha-2"
            >
              {{ event.http_status }}
            </span>
            <span class="text-xs text-n-slate-10">
              {{
                event.actor ||
                $t('CLIENT_SETTINGS.CURRENCY_MIGRATION.TIMELINE.SYSTEM')
              }}
            </span>
          </div>

          <p v-if="event.detail" class="mb-0 text-sm break-words text-n-slate-11">
            {{ event.detail }}
          </p>

          <button
            v-if="hasPayload(event)"
            type="button"
            class="flex items-center gap-1 text-xs w-fit text-n-blue-text hover:underline"
            :data-testid="`timeline-toggle-${event.id}`"
            @click="toggle(event.id)"
          >
            <Icon
              :icon="
                isExpanded(event.id)
                  ? 'i-lucide-chevron-down'
                  : 'i-lucide-chevron-right'
              "
              class="size-3.5"
            />
            {{ $t('CLIENT_SETTINGS.CURRENCY_MIGRATION.TIMELINE.TOGGLE') }}
          </button>

          <div
            v-if="isExpanded(event.id)"
            class="flex flex-col gap-2 mt-1"
            :data-testid="`timeline-payload-${event.id}`"
          >
            <div v-for="side in ['request', 'response']" :key="side">
              <span class="text-xs font-medium text-n-slate-11">
                {{
                  $t(
                    `CLIENT_SETTINGS.CURRENCY_MIGRATION.TIMELINE.${side.toUpperCase()}`
                  )
                }}
              </span>
              <pre
                class="p-2 mt-1 overflow-x-auto font-mono text-xs rounded-lg text-n-slate-12 bg-n-alpha-1"
                >{{ pretty(event[`${side}_payload`]) }}</pre
              >
            </div>
          </div>
        </div>
      </li>
    </ol>
  </section>
</template>
